export const compressImageTo1MB = async (
    file: File,
    sizeDividend: number = 1,
    maxSizeInMB: number = 1
): Promise<Blob> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = function (event: ProgressEvent<FileReader>) {
            const img = new Image();
            img.onload = function () {
                const canvas = document.createElement("canvas");
                const ctx = canvas.getContext("2d");

                if (!ctx) {
                    reject(new Error("Failed to get canvas context"));
                    return;
                }

                const newWidth = img.width / sizeDividend;
                const newHeight = img.height / sizeDividend;

                canvas.width = newWidth;
                canvas.height = newHeight;
                ctx.drawImage(img, 0, 0, newWidth, newHeight);

                canvas.toBlob(
                    async (blob) => {
                        if (!blob) {
                            reject(new Error("Compression failed"));
                            return;
                        }

                        // @ts-expect-error: name and lastModified are not standard Blob props
                        blob.name = file.name;
                        // @ts-expect-error: name and lastModified are not standard Blob props
                        blob.lastModified = file.lastModified;

                        if (blob.size < maxSizeInMB * 1024 * 1024) {
                            // If blob size is less than 1 MB, resolve with the blob
                            resolve(blob);
                        } else {
                            // If blob size is greater than or equal to 1 MB, recursively call compressImageTo1MB with updated sizeDividend
                            const nextSizeDividend = sizeDividend + 0.1;
                            const nextBlob = await compressImageTo1MB(file, nextSizeDividend);
                            resolve(nextBlob);
                        }
                    },
                    file.type,
                    0.1
                );
            };
            img.src = event.target?.result as string;
        };

        reader.onerror = () => reject(new Error("File reading failed"));
        reader.readAsDataURL(file);
    });
};

export const compressImageFileTo1MB = async (
    file: Parameters<typeof compressImageTo1MB>[0],
    maxSizeInMB: Parameters<typeof compressImageTo1MB>[1] = 1,
    sizeDividend: Parameters<typeof compressImageTo1MB>[2] = 1
): Promise<File> => {
    const compressedBlob = await compressImageTo1MB(file, sizeDividend, maxSizeInMB);
    return new File([compressedBlob], file.name, {
        type: compressedBlob.type,
        lastModified: file.lastModified
    });
};

export const isFileSizeGreaterThan = (file: File | Blob, maxSizeInMB: number): boolean => {
    const sizeInBytes = maxSizeInMB * 1024 * 1024;
    return file.size > sizeInBytes;
};

export const compressImageToGivenMB = async (
    file: File,
    maxSizeInMB: number = 1,
    quality: number = 0.8
): Promise<File> => {
    // Convert max size to bytes
    const maxBytes = maxSizeInMB * 1024 * 1024;

    // Read file as Data URL
    const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(new Error("File reading failed"));
        reader.readAsDataURL(file);
    });

    // Create an image element
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = () => reject(new Error("Image load failed"));
        image.src = dataUrl;
    });

    let sizeDividend = 1;
    let compressedFile: File | null = null;

    // Iteratively compress until under max size
    while (true) {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        if (!ctx) throw new Error("Failed to get canvas context");

        canvas.width = Math.round(img.width / sizeDividend);
        canvas.height = Math.round(img.height / sizeDividend);

        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, file.type, quality));
        if (!blob) throw new Error("Compression failed");

        compressedFile = new File([blob], file.name, {
            type: file.type,
            lastModified: file.lastModified
        });

        if (compressedFile.size <= maxBytes) break;

        // Increase sizeDividend slightly to reduce dimensions
        sizeDividend += 0.1;

        // Prevent infinite loop for extremely large files
        if (sizeDividend > 10) break;
    }

    return compressedFile!;
};
